import { env } from "@/config/env";

/**
 * The ONLY place this app performs HTTP.
 *
 * Every domain service goes through here, so auth, error shape, and abort
 * handling exist once rather than per call site. Nothing else in the app should
 * ever call `fetch` directly.
 */

export class ApiError extends Error {
	constructor(
		public readonly status: number,
		/** Machine-readable code from the Gateway, e.g. "seats_below_active". */
		public readonly code: string,
		message: string,
		/** The full body — some errors carry actionable detail. */
		public readonly body: Record<string, unknown> = {},
	) {
		super(message);
		this.name = "ApiError";
	}

	/** Signed in, but has no seat in any org. */
	get isNotOrgMember() {
		return this.status === 403 && this.code === "not_an_org_member";
	}
	get isUnauthorized() {
		return this.status === 401;
	}
	/** Tried to do something their role doesn't allow. */
	get isForbidden() {
		return this.status === 403;
	}
}

/** Access token store. In memory, refreshed via the Gateway's refresh cookie. */
let accessToken: string | null = null;
export const setAccessToken = (t: string | null) => {
	accessToken = t;
};
export const getAccessToken = () => accessToken;

interface RequestOptions {
	method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
	body?: unknown;
	query?: Record<string, string | number | undefined>;
	signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
	const url = new URL(path, env.apiBaseUrl);
	for (const [k, v] of Object.entries(query ?? {})) {
		if (v !== undefined && v !== "") url.searchParams.set(k, String(v));
	}
	return url.toString();
}

/**
 * Refresh the access token, collapsing concurrent attempts into one request.
 * Set by the auth service to avoid a circular import — http must not depend on
 * a service, since every service depends on http.
 */
let refreshFn: (() => Promise<boolean>) | null = null;
export const setRefreshHandler = (fn: () => Promise<boolean>) => {
	refreshFn = fn;
};

/**
 * Cross-tab, cross-caller single-flight refresh.
 *
 * The Gateway ROTATES the refresh token on every /api/auth/refresh and treats
 * reuse of an already-rotated one as a replay attack — it revokes the entire
 * token family, which signs the account out everywhere. So two refreshes racing
 * with the same token do not merely waste a call: the second one logs the user
 * out.
 *
 * That is what made a page reload sign people out. React StrictMode invokes the
 * boot effect TWICE in development, so `restore()` fired two /refresh calls with
 * the same cookie; the first rotated, the second looked like a replay, the
 * family was revoked, and the freshly-restored session died on arrival.
 *
 * Two layers, because they cover different races:
 *   - the in-process promise collapses concurrent callers in THIS tab, which is
 *     what StrictMode and two parallel 401s produce;
 *   - the Web Locks API serializes across TABS, where a shared in-process
 *     promise does not exist. By the time a waiting tab enters the lock, the
 *     cookie jar already holds the rotated token, so its own refresh is valid.
 *
 * Falls back to the in-process lock alone where navigator.locks is unavailable;
 * single-tab use is unaffected. Same approach as curoboard's ApiClient, and for
 * the same reason.
 */
const REFRESH_LOCK_NAME = "curo-dashboard-auth-refresh";

const supportsLocks = (): boolean =>
	typeof navigator !== "undefined" &&
	typeof (navigator as unknown as { locks?: { request?: unknown } }).locks
		?.request === "function";

let inFlightRefresh: Promise<boolean> | null = null;

export function refreshOnce(): Promise<boolean> {
	if (!refreshFn) return Promise.resolve(false);
	if (!inFlightRefresh) {
		const run = async (): Promise<boolean> => {
			// biome-ignore lint/style/noNonNullAssertion: guarded above
			const fn = refreshFn!;
			if (!supportsLocks()) return fn();
			return (
				navigator as unknown as {
					locks: {
						request: (
							n: string,
							cb: () => Promise<boolean>,
						) => Promise<boolean>;
					};
				}
			).locks.request(REFRESH_LOCK_NAME, fn);
		};
		inFlightRefresh = run().finally(() => {
			inFlightRefresh = null;
		});
	}
	return inFlightRefresh;
}

/**
 * The refresh endpoint reads `Authorization: Bearer …` as the REFRESH TOKEN.
 *
 * The Gateway serves two kinds of client from one route (auth.route.ts): mobile
 * and desktop send their refresh token in that header ("bearer mode"), while a
 * browser sends the httpOnly cookie. Attaching our ACCESS token here puts us in
 * bearer mode with the wrong token: the Gateway tries to verify an access token
 * as a refresh token, fails, and answers 401.
 *
 * That is why a tab left open showed 401 until it was reloaded. On boot there is
 * no access token in memory, so no header went up and the cookie was used; an
 * hour later the expired token was still in memory, went up as the header, and
 * every refresh failed — leaving the app on "Can't reach Curo" with a perfectly
 * valid session sitting in the cookie jar.
 */
const isRefresh = (path: string) => path.includes("/api/auth/refresh");

async function send(path: string, opts: RequestOptions): Promise<Response> {
	// FormData carries its own multipart Content-Type WITH a boundary the
	// browser generates. Setting the header ourselves would overwrite it with a
	// boundary-less value and the server would parse nothing — so form bodies
	// pass through untouched, headers included.
	const isForm =
		typeof FormData !== "undefined" && opts.body instanceof FormData;

	return fetch(buildUrl(path, opts.query), {
		method: opts.method ?? "GET",
		headers: {
			...(opts.body && !isForm ? { "Content-Type": "application/json" } : {}),
			...(accessToken && !isRefresh(path)
				? { Authorization: `Bearer ${accessToken}` }
				: {}),
		},
		// The Gateway sets an httpOnly refresh cookie; it must ride along.
		credentials: "include",
		body: isForm
			? (opts.body as FormData)
			: opts.body
				? JSON.stringify(opts.body)
				: undefined,
		signal: opts.signal,
	});
}

export async function request<T>(
	path: string,
	opts: RequestOptions = {},
): Promise<T> {
	let res = await send(path, opts);

	// Access tokens live 15 minutes, so a dashboard left open WILL 401
	// mid-session. Refresh once and retry rather than surfacing an error the
	// user can only fix by reloading. Never for the refresh endpoint itself,
	// which would recurse.
	if (res.status === 401 && !isRefresh(path)) {
		if (await refreshOnce()) res = await send(path, opts);
	}

	if (res.status === 204) return undefined as T;

	const text = await res.text();
	const parsed: unknown = text ? safeJson(text) : {};

	if (!res.ok) {
		const body = (parsed ?? {}) as Record<string, unknown>;
		throw new ApiError(
			res.status,
			typeof body.error === "string" ? body.error : "unknown_error",
			typeof body.message === "string"
				? body.message
				: `Request failed (${res.status})`,
			body,
		);
	}

	return parsed as T;
}

function safeJson(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return { message: text };
	}
}

export const http = {
	get: <T>(path: string, o?: Omit<RequestOptions, "method" | "body">) =>
		request<T>(path, { ...o, method: "GET" }),
	post: <T>(path: string, body?: unknown, o?: RequestOptions) =>
		request<T>(path, { ...o, method: "POST", body }),
	patch: <T>(path: string, body?: unknown, o?: RequestOptions) =>
		request<T>(path, { ...o, method: "PATCH", body }),
	put: <T>(path: string, body?: unknown, o?: RequestOptions) =>
		request<T>(path, { ...o, method: "PUT", body }),
	del: <T>(path: string, o?: RequestOptions) =>
		request<T>(path, { ...o, method: "DELETE" }),
	/** Multipart upload. Kept distinct so a caller cannot pass FormData by
	 *  accident to a JSON method and have it serialize to "[object FormData]". */
	postForm: <T>(path: string, form: FormData, o?: RequestOptions) =>
		request<T>(path, { ...o, method: "POST", body: form }),
};
